import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { useCategory } from "../context/CategoryContext";

const DuePaymentsPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [allCustomers, setAllCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [message, setMessage] = useState("");
  const [customer, setCustomer] = useState("");

  const { selectedCategory } = useCategory();

  // NEW: bulk selection state
  const [selectedIds, setSelectedIds] = useState([]); // _id[]
  const [selectAll, setSelectAll] = useState(false);
  const [bulkState, setBulkState] = useState(""); // e.g. "Paid" | "Lapsed" | "In Force" | "Overdue" | "Due"
  const [updating, setUpdating] = useState(false); // button spinner

  // ▶️ UI filters (range-ready; safe client-side only)
  const [filters, setFilters] = useState({
    dueDate: "",
    company: "",
    type: "",
    dueStatus: "",
  });

  const [modalData, setModalData] = useState(null);
  const { user } = useAuth();

  // ---------- helpers ----------
  const normalizeStatus = (s) => {
    if (typeof s === "string") return s;
    if (s && typeof s === "object") return s.state ?? "";
    return "";
  };

  const parseYMDLocal = (s) => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) {
      const d = new Date(s);
      return isNaN(d) ? null : d;
    }
    return new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
  };
  const startOfToday = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  };
  const daysDiffFromToday = (dueStr) => {
    const due = parseYMDLocal(dueStr);
    if (!due) return null;
    return Math.round((due.getTime() - startOfToday().getTime()) / 86400000);
  };
  const fmtDDMMYYYY = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
    return m ? `${m[3]}-${m[2]}-${m[1]}` : iso || "";
  };
  const sortByDue = (arr) => {
    const key = (d) => {
      if (d === null) return [2, 0];
      if (d >= 0) return [0, d];
      return [1, Math.abs(d)];
    };
    return [...arr].sort((a, b) => {
      const ka = key(a.__dueDays ?? daysDiffFromToday(a.dueDate));
      const kb = key(b.__dueDays ?? daysDiffFromToday(b.dueDate));
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
  };
  const classifyDue = (dueStr) => {
    const diff = daysDiffFromToday(dueStr);
    if (diff === null) return "Due";
    if (diff < 0) return "Overdue";
    if (diff === 0) return "Due Today";
    if (diff <= 7) return "Upcoming (7d)";
    if (diff <= 30) return "Upcoming (30d)";
  };
  const toCsv = (rows) => {
    if (!rows?.length)
      return "Customer Name,Customer Phone,Insurance Type,Company,Premium Amount,Due Date,Status\n";
    const headers = [
      "Customer Name",
      "Customer Phone",
      "Insurance Type",
      "Company",
      "Premium Amount",
      "Due Date",
      "Status",
      "Policy Number",
    ];
    const esc = (v) => {
      const s = String(v ?? "");
      return /[\",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const fmtDate = (s) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || "").trim());
      const human = m ? `${m[3]}-${m[2]}-${m[1]}` : s ? String(s) : "";
      return human ? `="${human}` : "";
    };
    const body = (rows || []).map((r) => {
      const phoneTxt = r.customerPhone ? `="${String(r.customerPhone).trim()}"` : "";
      const policyTxt = r.policyNumber ? `="${String(r.policyNumber).trim()}"` : "";
      const premium = Number.isFinite(Number(r.premium)) ? Number(r.premium) : "";
      const due = fmtDate(r.dueDate);
      const status = normalizeStatus(r.status) || "";
      return [
        r.name ?? "",
        phoneTxt,
        r.type ?? "",
        r.company ?? "",
        premium,
        due,
        status,
        policyTxt,
      ]
        .map(esc)
        .join(",");
    });
    return [headers.join(","), ...body].join("\n");
  };
  const downloadReport = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "due-payments.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateMessage = (cust) => {
    const type = cust.type || "your";
    const amt = Number(cust.premium || 0);
    const due = cust.dueDate || "the upcoming date";
    const status = normalizeStatus(cust.status).toLowerCase();

    if (status === "paid") {
      let msg = `Hi ${cust.name}! 👋\n\nThank you for your payment of ₹${amt.toLocaleString()} for your ${type} Insurance. Your policy is now marked as PAID.`;
      if (cust._id) {
        msg += `\n\nReceipt / Details: ${import.meta.env.VITE_API_URL}/payment/${cust._id}`;
      }
      return msg;
    }

    let msg = `Hi ${cust.name}! 👋\n\nYour ${type} Insurance premium of ₹${amt.toLocaleString()} is due on ${due}.`;
    if (cust._id) {
      msg += `\n\nPay Now: ${import.meta.env.VITE_API_URL}/payment/${cust._id}`;
    }
    return msg;
  };

  // ---------- fetch ----------
  useEffect(() => {
    const fetchDuePayments = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/Insurance-by-Category?insuranceType=${selectedCategory}&agentId=${user.id}`
        );
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();

        const formatted = (data || []).map((p) => {
          const dueStr = p?.policyDetails?.endDate
            ? String(p.policyDetails.endDate).slice(0, 10)
            : null;
          const __dueDays = daysDiffFromToday(dueStr);
          const statusStr = normalizeStatus(p.status) || "N/A"; // << force string

          return {
            _id: p._id || "Unknown",
            name: p.customerName || "Unknown",
            company: p.company || "Unknown",
            type: p.policyType || "N/A",
            premium: p.policyDetails?.premium,
            dueDate:
              (p.policyDetails?.endDate &&
                new Date(p.policyDetails.endDate).toISOString().split("T")[0]) ||
              "N/A",
            status: statusStr, // << keep as string
            customerPhone: p.customerPhone || "",
            policyNumber: p.policyNumber || "",
            category: p.insuranceType || "Unknown",
            __dueDays,
            __dueDateHuman: fmtDDMMYYYY(dueStr),
          };
        });

        const ordered = sortByDue(formatted);
        setAllCustomers(ordered);
        setFiltered(ordered);
      } catch (e) {
        console.error("Fetch error:", e);
      }
    };
    if (user?.id) fetchDuePayments();
  }, [user, selectedCategory]);

  // ---------- filtering ----------
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const next = { ...filters, [name]: value };
    setFilters(next);
    applyFilters(next);
  };

  const applyFilters = (f) => {
    const dueExact = f.dueDate ? parseYMDLocal(f.dueDate) : null;

    const out = allCustomers.filter((row) => {
      // date filter (optional)
      if (dueExact) {
        const rowDue = parseYMDLocal(row.dueDate);
        if (!rowDue || rowDue.getTime() !== dueExact.getTime()) return false;
      }

      if (f.company && row.company !== f.company) return false;
      if (f.type && row.type !== f.type) return false;

      // ✅ Filter by DB status as string
      if (f.dueStatus) {
        if (normalizeStatus(row.status) !== String(f.dueStatus)) return false;
      }

      return true;
    });

    setFiltered(out);
    setSelectedIds([]);
    setSelectAll(false);
  };

  const clearAll = () => {
    const reset = { dueDate: "", company: "", type: "", dueStatus: "" };
    setFilters(reset);
    setFiltered(allCustomers);
    setSelectedIds([]); // NEW
    setSelectAll(false); // NEW
  };

  // NEW: selection handlers
  const toggleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedIds(filtered.map((r) => r._id));
    } else {
      setSelectedIds([]);
    }
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // NEW: bulk update API call (same status for many)
  const bulkUpdateSelected = async () => {
    if (!selectedIds.length) return alert("Please select at least one policy.");
    if (!bulkState) return alert("Please choose a status to apply.");
    try {
      setUpdating(true);
      const agentId = user?.id || JSON.parse(localStorage.getItem("user"))?.id;
      const payload = {
        policyIds: selectedIds,
        agentId,
        status: { state: bulkState }, // if your backend expects object
      };

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/policies/bulk/update-same`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "Failed to update policies");
      }
      await res.json();

      // Optimistically reflect change in current tables (store string!)
      const applyLocal = (arr) =>
        arr.map((row) =>
          selectedIds.includes(row._id)
            ? { ...row, status: bulkState } // << keep as string
            : row
        );

      setAllCustomers((prev) => applyLocal(prev));
      setFiltered((prev) => applyLocal(prev));
      setSelectedIds([]);
      setSelectAll(false);
      alert("✅ Status updated for selected policies.");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to update: " + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const sendDueReminder = async (cust) => {
    try {
      const agentId = user?.id || JSON.parse(localStorage.getItem("user"))?.id;
      if (!agentId) return alert("Agent not found. Please login again.");
      if (!cust?.customerPhone) return alert("No phone number found for this customer.");
      const payload = {
        message: message || generateMessage(cust),
        phone: String(cust.customerPhone).replace(/\s+/g, ""),
        agentId,
        policyId: cust._id,
      };
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/v1/notification/send-notification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Failed to send notification");
      alert("✅ Reminder sent successfully!");
      setModalData(null);
    } catch (err) {
      console.error(err);
      alert("❌ Failed to send reminder");
    }
  };

  const uniquetypes = [
    ...new Set(
      allCustomers
        .filter((c) => c.category === selectedCategory)
        .map((c) => c.type)
    ),
  ];
  const uniquecompanies = [...new Set(allCustomers.map((c) => c.company))];

  // helper to show current row "Due/Overdue" chip (visual only)
  const visualDueStatus = (dueDate) => {
    const today = new Date().setHours(0, 0, 0, 0);
    const dueMs = dueDate ? new Date(dueDate).setHours(0, 0, 0, 0) : null;
    return dueMs && dueMs < today ? "Overdue" : "Due";
  };

  return (
    <div className="dashboard-layout d-flex due-page">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="main-content flex-grow-1">
        <div className="container py-4">
          {/* Title */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h2 className="due-title m-0">Due Payments</h2>
              <p className="due-sub m-0">
                Manage and track pending insurance payments and premiums
              </p>
            </div>

            <div className="d-flex gap-2">
              {/* NEW: bulk action compact bar (status + update) */}
              <div className="d-flex align-items-stretch gap-2">
                <select
                  className="form-select"
                  value={bulkState}
                  onChange={(e) => setBulkState(e.target.value)}
                  style={{ minWidth: 160 }}
                  title="Choose a status to apply"
                >
                  <option value="">Set status</option>
                  <option value="Paid">Paid</option>
                  <option value="OverDue">Over Due</option>
                  <option value="Due">Due</option>
                  {/* add any other states you store */}
                </select>
                <button
                  className="btn btn-pill teal"
                  onClick={bulkUpdateSelected}
                  disabled={!selectedIds.length || !bulkState || updating}
                  title={!selectedIds.length ? "Select policies first" : ""}
                >
                  {updating ? "Updating…" : "Update Selected"}
                </button>
              </div>

              <button className="btn btn-pill teal" onClick={downloadReport}>
                <i className="bi bi-download" />
              </button>
            </div>
          </div>

          {/* Filters bar */}
          <div className="card border-0 shadow-sm rounded-4 due-filters mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                {/* Due Date (single) */}
                <div className="col-12 col-md-3">
                  <label className="form-label">Due Date</label>
                  <div className="input-icon">
                    <i className="bi bi-calendar2-date" />
                    <input
                      type="date"
                      name="dueDate"
                      value={filters.dueDate}
                      onChange={handleFilterChange}
                      className="form-control filter-control"
                    />
                  </div>
                </div>

                {/* Insurance Company */}
                <div className="col-12 col-md-3">
                  <label className="form-label">Insurance Company</label>
                  <div className="select-caret">
                    <select
                      className="form-select filter-control"
                      name="company"
                      value={filters.company}
                      onChange={handleFilterChange}
                    >
                      <option value="">All</option>
                      {uniquecompanies.map((company) => (
                        <option key={company} value={company}>
                          {company}
                        </option>
                      ))}
                    </select>
                    <i className="bi bi-caret-down-fill" />
                  </div>
                </div>

                {/* Type */}
                <div className="col-12 col-md-2">
                  <label className="form-label">Type</label>
                  <div className="select-caret">
                    <select
                      className="form-select filter-control"
                      name="type"
                      value={filters.type}
                      onChange={handleFilterChange}
                    >
                      <option value="">All</option>
                      {selectedCategory !== "All" &&
                        uniquetypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                    </select>
                    <i className="bi bi-caret-down-fill" />
                  </div>
                </div>

                {/* Due Status */}
                <div className="col-12 col-md-2">
                  <label className="form-label">Due Status</label>
                  <div className="select-caret">
                    <select
                      className="form-select filter-control"
                      name="dueStatus"
                      value={filters.dueStatus}
                      onChange={handleFilterChange}
                    >
                      <option value="">All</option>
                      <option value="OverDue">OverDue</option>
                      <option value="Due">Due</option>
                      <option value="Paid">Paid</option>
                    </select>
                    <i className="bi bi-caret-down-fill" />
                  </div>
                </div>

                {/* Clear */}
                <div className="col-12 col-md-2 d-flex justify-content-md-end">
                  <button
                    className="btn btn-pill teal-outline w-100 filter-control-btn"
                    onClick={clearAll}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-hover due-table align-middle mb-0">
                  <thead>
                    <tr>
                      {/* NEW: select all */}
                      <th style={{ width: 36, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={toggleSelectAll}
                          aria-label="Select all"
                        />
                      </th>
                      <th>S.N</th>
                      <th>Name</th>
                      <th>Policy No.</th>
                      <th>Company</th>
                      <th>Premium</th>
                      <th>Due Date</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((cust, i) => {
                      const statusChip = normalizeStatus(cust.status);
                      const checked = selectedIds.includes(cust._id);
                      const sLower = statusChip.toLowerCase();
                      const badgeClass =
                        sLower === "overdue" || sLower === "overdue" // allow both spellings
                          ? "status-red"
                          : sLower === "due"
                          ? "status-amber"
                          : sLower === "paid"
                          ? "status-green"
                          : ""; // default no color

                      return (
                        <tr key={cust._id}>
                          {/* NEW: row checkbox */}
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleOne(cust._id)}
                              aria-label={`Select ${cust.name}`}
                            />
                          </td>

                          <td>{i + 1}</td>
                          <td className="fw-medium">{cust.name}</td>
                          <td className="text-muted">
                            {cust.policyNumber || "-"}
                          </td>
                          <td>{cust.company}</td>
                          <td className="text-teal fw-semibold">
                            ₹{Number(cust.premium || 0).toLocaleString()}
                          </td>
                          <td>{cust.dueDate}</td>
                          <td>{cust.type}</td>
                          <td>
                            <span className={`badge rounded-pill ${badgeClass}`}>
                              {statusChip || "-"}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-pill ghost-teal"
                              onClick={() => {
                                const s = normalizeStatus(cust.status);
                                const c = { ...cust, status: s }; // ensure string
                                setModalData(c);
                                setMessage(generateMessage(c));
                              }}
                            >
                              <i className="bi bi-bell me-2" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan="10" className="text-center text-muted py-4">
                          No records match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Modal */}
          {modalData && (
            <div className="reminder-modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>📨 Send Payment Reminder</h5>
                  <button onClick={() => setModalData(null)}>✖</button>
                </div>

                <div className="modal-summary">
                  <div>
                    <strong>{modalData.name}</strong>
                    <div>
                      {modalData.type} Insurance – {modalData.company}
                    </div>
                    <small>Due: {modalData.dueDate}</small>
                    <div>
                      <small>Mobile Number: {modalData.customerPhone}</small>
                    </div>
                  </div>
                  <div className="text-end">
                    <h5 className="text-blue">
                      ₹{Number(modalData.premium || 0).toLocaleString()}
                    </h5>
                    <span
                      className={`status ${normalizeStatus(
                        modalData.status
                      ).toLowerCase()}`}
                    >
                      {normalizeStatus(modalData.status)}
                    </span>
                  </div>
                </div>
                <div className="modal-body">
                  <label>Message Preview</label>
                  <textarea
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  {modalData?._id && (
                    <Link
                      to={`/payment/${modalData._id}`}
                      className="btn btn-link p-0"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Payment Page
                    </Link>
                  )}
                  <div className="modal-actions">
                    <button onClick={() => setModalData(null)} className="cancel">
                      Cancel
                    </button>
                    <button
                      className="send"
                      onClick={() => sendDueReminder(modalData)}
                    >
                      Send Reminder
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* /Modal */}
        </div>
      </div>
    </div>
  );
};

export default DuePaymentsPage;
